import {
  TwitterCrisisMonitorAttacksCountByTermsDTO,
  TwitterCrisisMonitorAttacksCountByTermsItemDTO,
} from '@/data/dto/twitter-crisis-monitor/attacks-count-by-terms.dto';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
  PaginatedRequestParamsDTO,
  ResponseMetaTypeDTO,
} from '@/data/dto/common.dto';
import store from '@/store';
import MOMABffService from '@/services/moma-bff.service';
import { AttacksCountByTermsStoreContract } from '@/data/store/attacks-count-by-terms.store.contract';
import { TwitterCrisisMonitorAttackDateFiltersDTO } from '@/data/dto/twitter-crisis-monitor/date-filters.dto';
import { FilterStoreContract } from '@/data/store/filter.store.contract';

type BasicState<T> = {
  loading: boolean;
  data: T | null;
  meta?: ResponseMetaTypeDTO;
  error: string | null;
  pagination?: PaginatedRequestParamsDTO;
};

type LocalState = BasicState<TwitterCrisisMonitorAttacksCountByTermsDTO>;

@Module({
  dynamic: true,
  namespaced: true,
  name: 'RealtimeAttacksCountByTerms',
  store,
})
export class AttacksCountByTermsStore
  extends VuexModule
  implements AttacksCountByTermsStoreContract, FilterStoreContract
{
  bffService = new MOMABffService();

  paginationLimit = 10;

  _state: LocalState = {
    loading: false,
    error: null,
    data: null,
    pagination: {
      offset: 0,
      limit: 10,
    },
  };

  get isLoading(): boolean {
    return this._state.loading;
  }

  get all(): TwitterCrisisMonitorAttacksCountByTermsItemDTO[] {
    if (!this._state.data) {
      return [];
    }

    return this._state.data.attacksCountByTerm;
  }

  get allForSelect() {
    return this.all.map((item) => item.term);
  }

  get maxAttacksCount(): number {
    return this.all[0].attacksCount;
  }

  get paginated(): TwitterCrisisMonitorAttacksCountByTermsItemDTO[] {
    const start = this._state.pagination?.offset || 0;
    const end = start + (this._state.pagination?.limit || this.paginationLimit);

    return this.all.slice(start, end);
  }

  get totalCount(): number {
    return this._state.meta?.totalCount || 0;
  }

  @Mutation
  updateAttacksCountByTermsState(payload: Partial<LocalState>): void {
    this._state = {
      ...this._state,
      ...payload,
    };
  }

  @Action
  async fetch(payload: {
    pagination?: PaginatedRequestParamsDTO;
    filters?: TwitterCrisisMonitorAttackDateFiltersDTO;
  }): Promise<void> {
    const { pagination } = payload;

    const validatedPagination = pagination || {
      limit: this.paginationLimit,
      offset: 0,
    };

    if (this._state.data) {
      this.updateAttacksCountByTermsState({
        pagination: validatedPagination,
      });
      return;
    }

    await this._getAttacksCountByTerms();
  }

  @Action
  async _getAttacksCountByTerms(): Promise<void> {
    this.updateAttacksCountByTermsState({
      loading: true,
      error: null,
      meta: undefined,
      pagination: {
        offset: 0,
        limit: 10,
      },
    });

    try {
      const response =
        await this.bffService.getTwitterCrisisMonitorRealtimeAttacksCountByTerms();
      this.updateAttacksCountByTermsState({
        data: response.data,
        meta: response.meta,
      });
    } catch (error: any) {
      this.updateAttacksCountByTermsState({
        error: error?.message,
      });
    } finally {
      this.updateAttacksCountByTermsState({
        loading: false,
      });
    }
  }
}
